import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Factory, Users, Receipt, BookOpen, Wallet,
  BarChart3, FileText, Settings, ClipboardList, Coins, Search, LogOut, Plus, Menu, X, UserCircle, CheckSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import TransactionModal from "@/components/TransactionModal";

const NAV = [
  { id: "dashboard", label: "Dashboard Kontrol", short: "Dashboard", icon: LayoutDashboard, path: "/" },
  { id: "panggil", label: "Panggil ID", short: "Panggil", icon: Search, path: "/panggil" },
  { id: "production", label: "Produksi", short: "Produksi", icon: Factory, path: "/production" },
  { id: "workers", label: "Tenaga Kerja", icon: Users, path: "/workers" },
  { id: "pricing", label: "Input Data", icon: Coins, path: "/pricing", editorOnly: true },
  { id: "bon", label: "Bon Barang", icon: Receipt, path: "/bon" },
  { id: "ledger", label: "Jurnal Umum", icon: BookOpen, path: "/ledger" },
  { id: "input-operator", label: "Input Operator", icon: ClipboardList, path: "/input-operator", editorOnly: true },
  { id: "payroll", label: "Payroll & Ongkos", icon: Wallet, path: "/payroll" },
  { id: "totalan", label: "Totalan", short: "Totalan", icon: ClipboardList, path: "/totalan" },
  { id: "analytics", label: "Analisis Produksi", icon: BarChart3, path: "/analytics" },
  { id: "report", label: "Laporan", icon: FileText, path: "/report" },
  { id: "persetujuan", label: "Persetujuan", icon: CheckSquare, path: "/persetujuan", editorOnly: true },
  { id: "admin", label: "Master & Admin", icon: Settings, path: "/admin" },
  { id: "account", label: "Akun Saya", icon: UserCircle, path: "/akun" },
];

const ROLE_LABEL = { OWNER: "OWNER", ACCOUNTING: "ACCOUNTING", ADMIN: "ADMIN", VIEWER: "PENGAWAS", PENCATAT: "PENCATAT" };

const OPERATOR_NAV = [
  { id: "op-home", label: "Beranda Modul", short: "Modul", icon: ClipboardList, path: "/operasional-jahitan" },
  { id: "op-history", label: "Riwayat Modul", short: "Riwayat", icon: BookOpen, path: "/operasional-jahitan/riwayat" },
  { id: "account", label: "Akun Saya", short: "Akun", icon: UserCircle, path: "/akun" },
];

export default function Layout({ children }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { user, logout, isEditor, isOperator } = useAuth();
  const [open, setOpen] = useState(false);
  const [trxOpen, setTrxOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isEditor) return;
    let alive = true;
    const fetchCount = () => api.get("/op/corrections?status=MENUNGGU")
      .then((r) => { if (alive) setPendingCount((r.data || []).length); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isEditor, loc.pathname]);

  const active = (path) => ((path === "/" || path === "/operasional-jahitan") ? loc.pathname === path : loc.pathname.startsWith(path));

  // Mobile card tables: salin teks <th> ke data-label tiap <td> agar CSS bisa menampilkan tabel sebagai kartu
  useEffect(() => {
    const label = () => document.querySelectorAll("main table").forEach((t) => {
      const ths = [...t.querySelectorAll("thead th")].map((h) => h.textContent.trim());
      if (!ths.length) return;
      t.querySelectorAll("tbody tr").forEach((tr) => { let i = 0; [...tr.children].forEach((td) => { if (!td.hasAttribute("colspan")) td.setAttribute("data-label", ths[i] || ""); i += td.colSpan || 1; }); });
    });
    label();
    const mo = new MutationObserver(() => { clearTimeout(mo._t); mo._t = setTimeout(label, 60); });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { clearTimeout(mo._t); mo.disconnect(); };
  }, []);

  if (isOperator) {
    const opNav = [
      { label: "Beranda", path: "/operasional-jahitan" },
      { label: "Riwayat", path: "/operasional-jahitan/riwayat" },
    ];
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-black flex flex-col" data-testid="operator-shell">
        <header className="sticky top-0 z-40 h-14 px-4 sm:px-6 flex items-center justify-between bg-white/85 dark:bg-[#1C1C1E]/85 backdrop-blur-xl border-b border-[#E5E5EA] dark:border-[#3A3A3C]">
          <button onClick={() => nav("/operasional-jahitan")} className="flex items-center gap-2 min-w-0" data-testid="operator-brand">
            <img src="/logo-meranggi.png" alt="Logo" className="w-8 h-8 rounded-full shrink-0" />
            <span className="font-semibold tracking-tight text-[#1D1D1F] dark:text-white truncate">Meranggi Ledger</span>
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            {opNav.map((n) => (
              <button key={n.path} onClick={() => nav(n.path)} data-testid={`operator-nav-${n.label.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${active(n.path) ? "bg-[#0071E3] text-white" : "text-[#6E6E73] dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10"}`}>{n.label}</button>
            ))}
            <span className="hidden md:block text-xs text-[#86868B] mx-1">{user?.name || user?.username} · PENCATAT</span>
            <button onClick={() => { logout(); nav("/login", { replace: true }); }} data-testid="operator-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"><LogOut className="w-4 h-4" />Keluar</button>
          </div>
        </header>
        <main className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-6">{children}</main>
      </div>
    );
  }

  const Sidebar = (
    <aside className="w-full lg:w-64 shrink-0 bg-[#FBFBFD] text-[#1D1D1F] border-r border-[#E5E5EA] flex flex-col h-full">
      <div className="px-5 py-4 lg:py-5 border-b border-[#E5E5EA] flex items-center gap-3">
        <img src="/logo-meranggi.png" alt="Logo Meranggi" className="w-11 h-11 rounded-full shrink-0" data-testid="sidebar-logo" />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-wide text-[#86868B]">CV. Meranggi Jaya</div>
          <div className="text-base font-semibold tracking-tight leading-tight">Meranggi Ledger</div>
          <div className="text-[10px] font-medium text-[#86868B]">Since 1981 · Production Control</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
        {(isOperator ? OPERATOR_NAV : NAV.filter((n) => !n.editorOnly || isEditor)).map((n) => {
          const Icon = n.icon;
          const isAct = active(n.path);
          return (
            <button
              key={n.id}
              data-testid={`nav-item-${n.id}`}
              onClick={() => { nav(n.path); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                isAct ? "bg-[#0071E3]/10 text-[#0071E3] font-semibold" : "text-[#6E6E73] hover:bg-[#E8E8ED] hover:text-[#1D1D1F]"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={isAct ? 2.25 : 1.75} />
              <span className="flex-1 text-left">{n.label}</span>
              {n.id === "persetujuan" && pendingCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold" data-testid="nav-persetujuan-badge">{pendingCount}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-4 py-4 border-t border-[#E5E5EA]">
        <div className="text-sm font-semibold truncate data-raw">{user?.name}</div>
        <div className="text-xs text-[#6E6E73] truncate data-raw">{user?.email}</div>
        <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-[#E8E8ED] text-[11px] font-semibold text-[#1D1D1F]">
          {ROLE_LABEL[user?.role] || user?.role}
        </div>
        <button
          data-testid="btn-logout"
          onClick={logout}
          className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#FF3B30] hover:bg-[#FFF1F0]"
        >
          <LogOut className="w-4 h-4" /> Keluar
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-[#F5F5F7]">
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative h-full w-[85vw] max-w-[288px]">{Sidebar}</div>
          <button className="absolute top-3 right-3 p-2 rounded-full bg-white/80 text-[#1D1D1F] shadow" onClick={() => setOpen(false)} aria-label="Tutup menu" data-testid="mobile-menu-close"><X className="w-6 h-6" /></button>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EA]">
          <div className="px-3 sm:px-6 h-14 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button className="lg:hidden p-2.5 -ml-1 text-[#1D1D1F] rounded-full active:bg-[#E8E8ED]" onClick={() => setOpen(true)} aria-label="Buka menu" data-testid="mobile-menu-button">
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <img src="/logo-meranggi.png" alt="Logo" className="w-8 h-8 rounded-full lg:hidden shrink-0" data-testid="header-logo" />
              <div className="min-w-0">
                <div className="text-sm font-semibold tracking-tight text-[#1D1D1F] leading-tight truncate">Ledger CV. Meranggi Jaya</div>
                <div className="text-[11px] text-[#6E6E73] leading-tight hidden sm:block">Production Control · WITA (Asia/Makassar)</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => nav("/akun")} className="hidden sm:flex items-center gap-2 px-2 py-1 rounded-full hover:bg-[#E8E8ED] text-left" data-testid="header-account-button">
                <div className="w-7 h-7 rounded-full bg-[#0071E3] text-white flex items-center justify-center text-xs font-bold data-raw">{(user?.name || "?").slice(0, 1).toUpperCase()}</div>
                <div className="leading-tight"><div className="text-xs font-semibold data-raw max-w-[120px] truncate">{user?.name}</div><div className="text-[10px] text-[#6E6E73]">{ROLE_LABEL[user?.role] || user?.role}</div></div>
              </button>
              {isEditor && (
                <Button data-testid="header-btn-quick-input" onClick={() => setTrxOpen(true)} className="bg-[#0071E3] hover:bg-[#0077ED] text-white h-9 px-3 sm:px-4 rounded-full font-medium">
                  <Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Input Transaksi</span>
                </Button>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-6 pb-24 lg:pb-6 max-w-[1600px] w-full mx-auto">{children}</main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/85 dark:bg-[#1C1C1E]/85 backdrop-blur-xl border-t border-[#E5E5EA] dark:border-[#3A3A3C] grid grid-cols-5 pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        {(isOperator ? OPERATOR_NAV : [NAV[0], NAV[1], NAV[2], NAV[8]]).map((n) => { const Icon = n.icon; const isAct = active(n.path); return (
          <button key={n.id} onClick={() => nav(n.path)} className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold ${isAct ? "text-[#0071E3]" : "text-[#6E6E73]"}`} data-testid={`bottom-nav-${n.id}`}>
            <Icon className="w-5 h-5" strokeWidth={isAct ? 2.25 : 1.75} />{n.short}
          </button>); })}
        <button onClick={() => setOpen(true)} className="flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold text-[#6E6E73]" data-testid="bottom-nav-menu"><Menu className="w-5 h-5" strokeWidth={1.75} />Menu</button>
      </nav>

      <TransactionModal open={trxOpen} onOpenChange={setTrxOpen} onDone={() => window.location.reload()} />
    </div>
  );
}
